import { PhotoSwipeLightbox } from "vendor";

/*
 * Opens the size chart image (snippets/size-chart-drawer.liquid and snippets/product-size-chart.liquid)
 * in a PhotoSwipe lightbox with the same UI as the product gallery lightbox (close + zoom buttons).
 *
 * PhotoSwipe renders outside the size chart drawer, whose focus trap treats any outside click as a
 * request to close the drawer — so the trap is paused while the lightbox is open and resumed on close.
 */

let lightbox = null;
let activeDrawer = null;

function getLightbox() {
  if (lightbox) {
    return lightbox;
  }

  lightbox = new PhotoSwipeLightbox({
    pswpModule: () => import("photoswipe"),
    bgOpacity: 1,
    // The size guide is a graphic with small text: make the zoom button always produce a
    // meaningful jump relative to the fitted size, whatever the viewport/image ratio is
    secondaryZoomLevel: (zoomLevels) => Math.min(zoomLevels.fit * 2.5, 4),
    maxZoomLevel: (zoomLevels) => Math.min(zoomLevels.fit * 5, 5),
    closeTitle: window.themeVariables.strings.closeGallery,
    zoomTitle: window.themeVariables.strings.zoomGallery,
    errorMsg: window.themeVariables.strings.errorGallery,
    arrowPrev: false,
    arrowNext: false,
    counter: false,
    close: false,
    zoom: false
  });

  lightbox.on("uiRegister", () => {
    lightbox.pswp.ui.registerElement({
      name: "zoom-button",
      className: "circle-button circle-button--xl hover:animate-icon-block",
      ariaLabel: window.themeVariables.strings.zoomGallery,
      order: 1,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" width="16" class="icon" viewBox="0 0 14 14">
          <path d="M9.432 9.432a4.94 4.94 0 1 1-6.985-6.985 4.94 4.94 0 0 1 6.985 6.985Zm0 0L13 13" fill="none" stroke="currentColor" stroke-linecap="square"/>
          <path d="M6 3.5V6m0 2.5V6m0 0H3.5h5" fill="none" stroke="currentColor"/>
        </svg>
      `,
      onClick: () => {
        lightbox.pswp.toggleZoom();
      }
    });

    lightbox.pswp.ui.registerElement({
      name: "close-button",
      className: "circle-button circle-button--xl hover:animate-icon-block",
      ariaLabel: window.themeVariables.strings.closeGallery,
      order: 2,
      isButton: true,
      html: `
        <svg aria-hidden="true" focusable="false" fill="none" width="16" class="icon" viewBox="0 0 16 16">
          <path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1"/>
        </svg>
      `,
      onClick: () => {
        lightbox.pswp.close();
      }
    });
  });

  lightbox.addFilter("thumbEl", (thumbEl, data) => data.thumbnailElement || thumbEl);

  lightbox.on("destroy", () => {
    activeDrawer?.focusTrap?.unpause();
    activeDrawer = null;
  });

  lightbox.init();
  return lightbox;
}

document.addEventListener("click", (event) => {
  const link = event.target.closest("a.size-chart__image-link");

  if (!link) {
    return;
  }

  event.preventDefault();

  const image = link.querySelector("img");

  let width = parseInt(link.getAttribute("data-pswp-width"), 10) || 0;
  let height = parseInt(link.getAttribute("data-pswp-height"), 10) || 0;

  if (!width || !height) {
    width = image?.naturalWidth || 1600;
    height = image?.naturalHeight || 1600;
  } else if (image?.naturalWidth > 0 && image?.naturalHeight > 0) {
    // The loaded thumbnail is the source of truth for the aspect ratio; a wrong declared
    // ratio would make PhotoSwipe distort the image to fit its slide box
    height = Math.round(width * (image.naturalHeight / image.naturalWidth));
  }

  activeDrawer = link.closest("x-drawer");
  activeDrawer?.focusTrap?.pause();

  getLightbox().loadAndOpen(0, [
    {
      src: link.href,
      msrc: image?.currentSrc || image?.src,
      width: width,
      height: height,
      alt: image?.alt || "",
      thumbnailElement: image
    }
  ]);
});
